import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/axios'; 
import { useToast } from '../hooks/use-toast';
import { Card, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Loader2, FileText, Send } from 'lucide-react';

// --- AUTHENTICATED MEDIA COMPONENT ---
// Securely fetches images/videos without leaking JWT tokens in HTML attributes
const AuthenticatedMedia = ({ chatId, messageId }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl;
    
    // Fetch as blob to securely pass the Authorization header via Axios
    api.get(`/whatsapp/messages/${chatId}/media/${messageId}`, { responseType: 'blob' })
      .then(response => {
        setMimeType(response.headers['content-type']);
        objectUrl = URL.createObjectURL(response.data);
        setMediaUrl(objectUrl);
      })
      .catch(() => setError(true));

    // Cleanup memory to prevent browser memory leaks on large chats
    return () => { 
      if (objectUrl) URL.revokeObjectURL(objectUrl); 
    };
  }, [chatId, messageId]);

  if (error) {
    return <div className="text-xs text-red-400 mt-2 bg-black/5 p-2 rounded">Media unavailable or expired</div>;
  }
  
  if (!mediaUrl) {
    return (
      <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin"/> Loading media...
      </div>
    );
  }

  if (mimeType?.includes('image')) {
    return <img src={mediaUrl} alt="Attachment" className="mt-2 rounded-lg max-h-64 object-contain bg-black/5" />;
  }
  
  if (mimeType?.includes('video')) {
    return <video src={mediaUrl} controls className="mt-2 rounded-lg max-h-64 bg-black/5" />;
  }
  
  // Fallback for PDFs/Documents
  return (
    <a href={mediaUrl} download={`document-${messageId}`} className="mt-2 flex items-center gap-2 text-sm text-blue-600 underline hover:text-blue-800">
      <FileText className="w-4 h-4" /> Download Document
    </a>
  );
};

export default function WhatsAppInbox() {
  const { toast } = useToast();
  
  // State
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Loaders
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  
  // Inputs
  const [replyText, setReplyText] = useState('');
  
  const messagesEndRef = useRef(null);

  // 1. Fetch all WhatsApp Chats
  const fetchChats = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/chats');
      if (data.success) setChats(data.chats);
    } catch (error) {
      toast({ 
        title: 'Connection Error', 
        description: 'WhatsApp might be disconnected. Check your status.', 
        variant: 'destructive' 
      });
    } finally {
      setLoadingChats(false);
    }
  }, [toast]);

  // 2. Fetch Thread for specific chat
  const fetchThread = useCallback(async (chatId) => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/whatsapp/messages/${chatId}`);
      if (data.success) {
        setMessages(data.messages); 
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load messages.', variant: 'destructive' });
    } finally {
      setLoadingMessages(false);
    }
  }, [toast]);

  // 3. Handle Outbound Message Reply
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedChat) return;

    setSendingReply(true);
    try {
      // Send to the secure backend route we built
      const response = await api.post('/whatsapp/send-text', {
        number: selectedChat.id, // The WhatsApp ID acts as the phone number here
        message: replyText.trim()
      });

      if (response.data.success) {
        setReplyText('');
        // Re-fetch the thread to show the newly sent message
        await fetchThread(selectedChat.id);
      }
    } catch (error) {
      toast({ 
        title: 'Send Failed', 
        description: error.response?.data?.error || 'Could not send message. Check balance.', 
        variant: 'destructive' 
      });
    } finally {
      setSendingReply(false);
    }
  };

  // Lifecycle hooks
  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  useEffect(() => {
    if (selectedChat) fetchThread(selectedChat.id);
  }, [selectedChat, fetchThread]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages load
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto h-[calc(100vh-4rem)] flex flex-col">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <MessageSquare className="h-8 w-8 text-green-600" /> WhatsApp Inbox
        </h1>
        <p className="text-muted-foreground">Live synchronization with your connected WhatsApp device.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-grow min-h-0">
        
        {/* LEFT PANE: Chat List */}
        <Card className="col-span-1 flex flex-col min-h-0 shadow-sm border-t-4 border-t-green-600">
          <CardHeader className="py-4 border-b">
            <CardTitle className="text-lg">Recent Conversations</CardTitle>
          </CardHeader>
          <ScrollArea className="flex-grow">
            {loadingChats ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
            ) : chats.length > 0 ? (
              <div className="flex flex-col">
                {chats.map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => setSelectedChat(chat)}
                    className={`p-4 text-left border-b hover:bg-gray-50 transition-colors ${
                      selectedChat?.id === chat.id ? 'bg-green-50 border-l-4 border-l-green-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="font-semibold text-gray-800 truncate">{chat.name}</div>
                      {chat.unreadCount > 0 && (
                        <Badge className="bg-green-600 ml-2">{chat.unreadCount}</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate mt-1">
                      {chat.lastMessage || 'Media message'}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground text-sm">
                No chats found. Scan your QR code to sync.
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* RIGHT PANE: Chat Thread & Reply Box */}
        <Card className="col-span-1 md:col-span-2 flex flex-col min-h-0 shadow-md">
          {selectedChat ? (
            <>
              {/* Header */}
              <CardHeader className="py-4 border-b bg-slate-50 flex-shrink-0">
                <CardTitle className="text-lg">{selectedChat.name}</CardTitle>
              </CardHeader>
              
              {/* Message History */}
              <ScrollArea className="flex-grow p-4 bg-[#efeae2]">
                {loadingMessages ? (
                  <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isMe = msg.fromMe; 
                      return (
                        <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex max-w-[85%] md:max-w-[70%] gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                            
                            <div className={`p-3 rounded-xl shadow-sm relative ${
                              isMe 
                                ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-none' 
                                : 'bg-white text-gray-900 rounded-tl-none'
                            }`}>
                              
                              {/* Render Images/Documents */}
                              {msg.hasMedia && (
                                <AuthenticatedMedia chatId={selectedChat.id} messageId={msg.id} />
                              )}

                              {/* Render Text */}
                              {msg.body && (
                                <p className="whitespace-pre-wrap text-sm leading-relaxed mt-1">
                                  {msg.body}
                                </p>
                              )}
                              
                              <div className="text-[10px] mt-1 flex justify-end text-gray-500">
                                {new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                            
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Reply Input Area */}
              <div className="p-4 bg-white border-t">
                <form onSubmit={handleSendReply} className="flex gap-2 items-end">
                  <textarea
                    className="flex-grow min-h-[44px] max-h-32 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Type a message..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      // Allow sending with Enter (but Shift+Enter for new line)
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply(e);
                      }
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!replyText.trim() || sendingReply}
                    className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingReply ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </form>
              </div>

            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8 text-center bg-gray-50/50">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-lg font-medium text-gray-600">WhatsApp Web Synced</p>
              <p className="text-sm mt-1">Select a chat to view messages, images, and documents.</p>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}